import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { subscriptionApi } from '../services/api';
import { isBillingComplete } from './PaymentPage';
import Modal, { useModal } from '../components/Modal';
import LoadingOverlay from '../components/LoadingOverlay';
import { useNavigate } from 'react-router-dom';

const IVA = 0.22;

interface PlanMeta { icon: string; period: string; features: string[]; }
// Allineato a config/plans.php sul backend. Nessun riferimento a
// sponsorizzazioni, budget o gruppi di annunci: l'utente vede solo il
// risultato, non il meccanismo.
const PLAN_META: Record<string, PlanMeta> = {
  trial:   { icon: 'science',            period: 'Una tantum · 8 giorni', features: ['1 post promosso', 'Visibilità su Facebook e Instagram', 'Ottimizzazione automatica', 'Durata 8 giorni'] },
  base:    { icon: 'rocket_launch',      period: 'Abbonamento mensile',   features: ['Fino a 2 post promossi insieme', 'Visibilità su Facebook e Instagram', 'Ottimizzazione avanzata', 'Risultati in tempo reale'] },
  medium:  { icon: 'star',              period: 'Abbonamento mensile',   features: ['Fino a 3 post promossi insieme', 'Visibilità su Facebook e Instagram', 'Ottimizzazione avanzata', 'Risultati in tempo reale'] },
  premium: { icon: 'workspace_premium', period: 'Abbonamento mensile',   features: ['Fino a 4 post promossi insieme', 'Visibilità su Facebook e Instagram', 'Ottimizzazione avanzata', 'Risultati in tempo reale'] },
};
const ORDER = ['trial', 'base', 'medium', 'premium'];

export default function PlansPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { modal, show, close } = useModal();
  const [plans, setPlans] = useState<any[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    subscriptionApi.plans()
      .then(r => setPlans(r.data))
      .catch(() => show({ title: 'Errore', message: 'Impossibile caricare i piani. Riprova tra poco.', type: 'error', onConfirm: close }))
      .finally(() => setPlansLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // La carta NON si chiede qui: la pagina di pagamento usa quella salvata o
  // la fa inserire sul posto. Nessun rimando al profilo.
  const handleSelect = (plan: any) => {
    if (!isBillingComplete(user)) {
      show({
        title: 'Dati di fatturazione mancanti',
        message: 'Completa i dati di fatturazione prima di scegliere un piano.',
        type: 'warning',
        confirmText: 'Vai alla fatturazione',
        onConfirm: () => { close(); navigate('/billing'); },
      });
      return;
    }
    if (!user?.has_facebook && !user?.has_instagram) {
      show({
        title: 'Account non collegato',
        message: 'Collega prima i tuoi social per attivare un piano.',
        type: 'warning',
        onConfirm: close,
      });
      return;
    }
    navigate(`/payment?plan=${plan.id}&price=${plan.price}`);
  };

  const sorted = [...plans].sort((a, b) => ORDER.indexOf(a.id) - ORDER.indexOf(b.id));

  return (
    <div className="page">
      {plansLoading && <LoadingOverlay title="Caricamento dei piani..." icon="workspace_premium" />}
      {modal && (
        <Modal
          isOpen
          title={modal.title}
          message={modal.message}
          type={modal.type}
          confirmText={modal.confirmText}
          cancelText={modal.cancelText}
          onConfirm={modal.onConfirm}
          onCancel={modal.onCancel ?? close}
        />
      )}

      <div style={{ textAlign: 'center' }}>
        <h1>Scegli il tuo piano</h1>
        <p className="sub" style={{ marginTop: 6 }}>Seleziona il piano più adatto alla tua attività</p>
      </div>

      {!isBillingComplete(user) && (
        <div className="alert warning" style={{ cursor: 'pointer' }} onClick={() => navigate('/billing')}>
          <span className="mi mi-18">info</span>
          <span>Completa i dati di fatturazione per attivare un piano. <strong>Vai alla fatturazione →</strong></span>
        </div>
      )}
      {!user?.has_facebook && !user?.has_instagram && (
        <div className="alert warning">
          <span className="mi mi-18">info</span>
          <span>Collega prima i tuoi social per attivare un piano.</span>
        </div>
      )}

      {sorted.map(plan => {
        const m = PLAN_META[plan.id];
        const isCurrent = user?.subscription_plan === plan.id && user?.subscription_status === 'active';
        // Il piano consigliato è Medium (scelta commerciale, 2026-10-22)
        const isPremium = plan.id === 'medium';
        const iva = Math.round(plan.price * IVA * 100) / 100;
        const total = plan.price + iva;

        return (
          <div key={plan.id} className={`plan-card${isPremium && !isCurrent ? ' recommended' : ''}${isCurrent ? ' active-plan' : ''}`}>

            {isPremium && !isCurrent && <div className="plan-tag">CONSIGLIATO</div>}
            {isCurrent && <div className="plan-tag success-tag">PIANO ATTUALE</div>}

            <div className="plan-header" style={{ marginTop: (isPremium && !isCurrent) || isCurrent ? 8 : 0 }}>
              <div className="plan-left">
                <span className="mi mi-22 plan-icon">{m?.icon}</span>
                <div>
                  <div className="plan-name">{plan.name}</div>
                  <div className="plan-period">{m?.period}</div>
                </div>
              </div>
              <div className="plan-price-wrap">
                <div className="plan-price-val">€{plan.price}</div>
                <div className="plan-price-sub">{plan.id === 'trial' ? 'una tantum' : '/mese'}</div>
              </div>
            </div>

            {/* IVA note */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                + IVA 22% = <strong style={{ color: 'var(--text-2)' }}>€{total.toFixed(2)}</strong> totali
              </span>
            </div>

            <div className="plan-separator" />

            <div className="plan-features">
              {m?.features.map((f, i) => (
                <div key={i} className="plan-feat">
                  <span className="mi mi-16" style={{ color: 'var(--success)' }}>check_circle</span>
                  {f}
                </div>
              ))}
            </div>

            {isCurrent ? (
              <div className="plan-active-label">
                <span className="mi mi-18">check_circle</span> Piano attivo
              </div>
            ) : (
              <button
                className={`btn ${isPremium ? 'btn-primary' : 'btn-secondary'}`}
                style={{ fontSize: 13, padding: '11px 16px' }}
                onClick={() => handleSelect(plan)}
              >
                {plan.id === 'trial' ? 'Prova ora' : 'Abbonati'}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
